// The office actions on ONE SAFed test job (Session BDB): book it (engineer, date, second engineer), put the authorisation in a Gmail
// draft, record the signed copy and how it reached us, draft the results, draft the invoice, and see the visit's photographs.
// Every customer-facing output is a DRAFT the office reads and sends (Gmail) or checks and authorises (Xero). Nothing is sent here.
import { useState } from "react";
import { api } from "../../../../lib/api";
import type { SafedAssetJobView } from "../../../../lib/api";
import { formatDate } from "../../../../lib/format";
import { ConfirmButton } from "../../../../components/ConfirmButton";
import { toneTextClass } from "../../../../components/StatusPill";
import type { SafedInvoicePlanView } from "../../../../lib/safedFlowTypes";
import { btnCls, errorText, inputCls, poundsFromPence, quietBtnCls } from "./safedUi";

interface Props {
  job: SafedAssetJobView;
  cleared: boolean;
  engineers: { id: string; name: string }[];
  onChanged: () => void | Promise<void>;
}

type Pending =
  | { kind: "auth" | "results"; to: string | null; subject: string; attachments: string[]; notes: string[] }
  | { kind: "invoice"; plan: SafedInvoicePlanView; notes: string[] };

const VIA_LABEL: Record<string, string> = { email: "email", post: "post", in_person: "in person", other: "another way" };

export function SafedJobActions({ job, cleared, engineers, onChanged }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [auth, setAuth] = useState<{ name: string; role: "duty_holder" | "authorised_signatory"; signedOn: string; via: "email" | "post" | "in_person" | "other"; link: string } | null>(null);
  const [book, setBook] = useState<{ engineerId: string; day: string; second: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const stress = job.tests.some((t) => t.cls === "STRESS");
  const done = job.status === "complete";
  const open = job.status === "scheduled" || job.status === "in_progress";

  async function run(fn: () => Promise<void>, fallback: string) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(errorText(err, fallback));
    } finally {
      setBusy(false);
    }
  }

  const previewDelivery = (kind: "auth" | "results") =>
    run(async () => {
      const res = await (kind === "auth" ? api.safedFlow.authorisationDraft(job.jobId, true) : api.safedFlow.resultsDraft(job.jobId, true));
      if (res.preview) setPending({ kind, to: res.to, subject: res.subject, attachments: res.attachments, notes: res.notes });
    }, kind === "auth" ? "Could not prepare the authorisation draft." : "Could not prepare the results draft.");

  const commit = () =>
    run(async () => {
      if (!pending) return;
      if (pending.kind === "invoice") {
        const res = await api.safedFlow.invoiceDraft(job.jobId, false);
        if (!res.preview) setNotice(`A Xero draft invoice${res.invoiceNumber ? ` (${res.invoiceNumber})` : ""} was raised. It is not authorised or sent: check it in Xero.`);
      } else {
        const res = await (pending.kind === "auth" ? api.safedFlow.authorisationDraft(job.jobId, false) : api.safedFlow.resultsDraft(job.jobId, false));
        if (!res.preview) setNotice(`A Gmail draft to ${res.to} is ready for you to read and send.`);
      }
      setPending(null);
      await onChanged();
    }, "Could not make that.");

  const previewInvoice = () =>
    run(async () => {
      const res = await api.safedFlow.invoiceDraft(job.jobId, true);
      if (res.preview) setPending({ kind: "invoice", plan: res.plan, notes: res.notes });
    }, "Could not prepare the invoice draft.");

  const saveAuth = () =>
    run(async () => {
      if (!auth) return;
      await api.safedJobs.setAuthorisation(job.jobId, {
        signatoryName: auth.name,
        signatoryRole: auth.role,
        signedAt: new Date(`${auth.signedOn}T12:00:00`).toISOString(),
        receivedVia: auth.via,
        documentUrl: auth.link.trim() || null,
      });
      setAuth(null);
      await onChanged();
    }, "Could not record the authorisation. Check the name and the date.");

  const saveBooking = () =>
    run(async () => {
      if (!book) return;
      await api.schedule.reassign(job.jobId, {
        ...(book.engineerId ? { assignedTo: book.engineerId } : {}),
        ...(book.day ? { scheduledFor: new Date(`${book.day}T09:00:00`).toISOString() } : {}),
      });
      if (book.second) await api.schedule.addCrew(job.jobId, book.second);
      setBook(null);
      await onChanged();
    }, "Could not book that. Check the engineer and the date.");

  return (
    <div className="mt-2">
      {error && <p role="alert" className="text-sm text-ele-danger">{error}</p>}
      {notice && <p role="status" className="text-sm text-ele-body">{notice}</p>}
      <p className="text-ele-body">
        {job.quoteId ? `Quotation ${job.quoteRef ?? ""}: ${job.quoteAccepted ? "accepted" : "not yet recorded as accepted"}.` : "No SAFed quotation is linked to this job."}
        {job.authDraftedAt ? ` Authorisation drafted ${formatDate(job.authDraftedAt)}.` : ""}
        {job.resultsDraftedAt ? ` Results drafted ${formatDate(job.resultsDraftedAt)}.` : ""}
        {job.invoiceNumber ? ` Xero draft ${job.invoiceNumber}.` : ""}
      </p>
      {job.authorisation && (
        <p className="text-ele-body">
          Signed copy: {job.authorisation.signatoryName}, received by {VIA_LABEL[job.authorisation.receivedVia ?? "other"] ?? "another way"}
          {job.authorisation.hasDocument ? ", copy filed" : ""}.
        </p>
      )}
      {stress && job.status === "scheduled" && (job.engineerCount ?? 0) < 2 && (
        <p className={toneTextClass("amber")}>A stress test needs two engineers. Add the second engineer below.</p>
      )}

      <div className="mt-2 flex flex-wrap gap-2">
        {job.status === "scheduled" && (
          <button type="button" className={btnCls} onClick={() => setBook({ engineerId: "", day: "", second: "" })}>Book engineers and date</button>
        )}
        {open && (
          <button type="button" className={btnCls} onClick={() => setAuth({ name: job.authorisation?.signatoryName ?? "", role: "duty_holder", signedOn: new Date().toISOString().slice(0, 10), via: "email", link: "" })}>
            {job.authorisation ? "Correct authorisation" : "Record signed authorisation"}
          </button>
        )}
        {open && (
          <button type="button" className={btnCls} disabled={!cleared || busy} title={cleared ? undefined : "The wording is still a draft awaiting solicitor review"} onClick={() => void previewDelivery("auth")}>
            Draft the authorisation
          </button>
        )}
        {done && (
          <button type="button" className={btnCls} disabled={busy} onClick={() => void previewDelivery("results")}>Draft the results email</button>
        )}
        {done && !job.invoiceNumber && (
          <button type="button" className={btnCls} disabled={busy} onClick={() => void previewInvoice()}>Draft the invoice</button>
        )}
        {job.authorisation && job.status === "scheduled" && (
          <ConfirmButton
            label="Remove authorisation"
            confirmLabel="Confirm: remove it"
            className="min-h-11 px-3 font-menu text-xs uppercase tracking-wide text-ele-danger"
            onConfirm={() => void api.safedJobs.removeAuthorisation(job.jobId).then(() => onChanged())}
          />
        )}
      </div>

      {pending && (
        <div className="mt-2 rounded-ele border border-ele-green px-3 py-2">
          {pending.kind === "invoice" ? (
            <>
              <p className="text-ele-strong">A Xero draft for {pending.plan.customerName}, reference {pending.plan.reference}:</p>
              <ul className="list-disc pl-5 text-ele-body">
                {pending.plan.lines.map((l) => (
                  <li key={l.description}>{l.description}: {poundsFromPence(Math.round(l.amountExVat * 100))}</li>
                ))}
              </ul>
              <p className="text-ele-strong">Total {poundsFromPence(Math.round(pending.plan.totalExVat * 100))} ex VAT.</p>
            </>
          ) : (
            <>
              <p className="text-ele-strong">{pending.to ? `A Gmail draft to ${pending.to}.` : "There is no email address for this yet. Add the customer's billing email first."}</p>
              <p className="text-ele-body">Subject: {pending.subject}. Attached: {pending.attachments.join(", ")}.</p>
            </>
          )}
          {pending.notes.map((n) => (
            <p key={n} className="text-xs text-ele-muted">{n}</p>
          ))}
          <p className="text-xs text-ele-muted">{pending.kind === "invoice" ? "It is a draft. Nobody here authorises or sends it." : "It goes into Gmail as a draft. You read it and send it."}</p>
          <div className="mt-2 flex gap-2">
            <button type="button" className={btnCls} disabled={busy || (pending.kind !== "invoice" && !pending.to)} onClick={() => void commit()}>
              {pending.kind === "invoice" ? "Raise the draft" : "Make the draft"}
            </button>
            <button type="button" className={quietBtnCls} onClick={() => setPending(null)}>Cancel</button>
          </div>
        </div>
      )}

      {auth && (
        <div className="mt-2 space-y-2">
          <label className="block text-ele-body">
            Signatory name
            <input className={inputCls} value={auth.name} onChange={(e) => setAuth({ ...auth, name: e.target.value })} />
          </label>
          <label className="block text-ele-body">
            Signed as
            <select className={inputCls} value={auth.role} onChange={(e) => setAuth({ ...auth, role: e.target.value as typeof auth.role })}>
              <option value="duty_holder">The duty holder</option>
              <option value="authorised_signatory">Authorised to sign for the duty holder</option>
            </select>
          </label>
          <label className="block text-ele-body">
            Date signed
            <input type="date" className={inputCls} value={auth.signedOn} onChange={(e) => setAuth({ ...auth, signedOn: e.target.value })} />
          </label>
          <label className="block text-ele-body">
            How the signed copy reached us
            <select className={inputCls} value={auth.via} onChange={(e) => setAuth({ ...auth, via: e.target.value as typeof auth.via })}>
              <option value="email">Email</option>
              <option value="post">Post</option>
              <option value="in_person">In person</option>
              <option value="other">Another way</option>
            </select>
          </label>
          <label className="block text-ele-body">
            Link to the filed copy (optional)
            <input className={inputCls} value={auth.link} onChange={(e) => setAuth({ ...auth, link: e.target.value })} />
          </label>
          <div className="flex gap-2">
            <button type="button" className={btnCls} disabled={busy || auth.name.trim().length < 2} onClick={() => void saveAuth()}>Save</button>
            <button type="button" className={quietBtnCls} onClick={() => setAuth(null)}>Cancel</button>
          </div>
        </div>
      )}

      {book && (
        <div className="mt-2 space-y-2">
          <label className="block text-ele-body">
            Engineer
            <select className={inputCls} value={book.engineerId} onChange={(e) => setBook({ ...book, engineerId: e.target.value })}>
              <option value="">Leave as it is</option>
              {engineers.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </label>
          <label className="block text-ele-body">
            Second engineer (needed for a stress test)
            <select className={inputCls} value={book.second} onChange={(e) => setBook({ ...book, second: e.target.value })}>
              <option value="">None</option>
              {engineers.filter((e) => e.id !== book.engineerId).map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </label>
          <label className="block text-ele-body">
            Day
            <input type="date" className={inputCls} value={book.day} onChange={(e) => setBook({ ...book, day: e.target.value })} />
          </label>
          <div className="flex gap-2">
            <button type="button" className={btnCls} disabled={busy || (!book.engineerId && !book.day && !book.second)} onClick={() => void saveBooking()}>Save the booking</button>
            <button type="button" className={quietBtnCls} onClick={() => setBook(null)}>Cancel</button>
          </div>
        </div>
      )}

      {(job.photoCount ?? 0) > 0 && (
        <div className="mt-2">
          <p className="text-ele-body">Photographs from the visit ({job.photoCount} of 6):</p>
          <div className="mt-1 grid grid-cols-3 gap-2">
            {Array.from({ length: job.photoCount ?? 0 }).map((_, i) => (
              <a key={i} href={`/api/jobs/${encodeURIComponent(job.jobId)}/photos/${i}`} target="_blank" rel="noreferrer" className="block aspect-square overflow-hidden rounded-ele border border-ele-purple-accent">
                <img src={`/api/jobs/${encodeURIComponent(job.jobId)}/photos/${i}`} alt={`Visit photograph ${i + 1}`} loading="lazy" className="h-full w-full object-cover" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
